import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, PillButton, EmptyState } from '../../ui';
import { ReactComponent as ListIcon } from '../../assets/icons/list.svg';
import './AdminLayout.css';

const PAGE_SIZE = 50;

function Audit() {
  const { t } = useTranslation();
  const { orgId } = useOutletContext();

  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback((p) => {
    setLoading(true);
    api.get(`/org/${orgId}/audit`, { params: { page: p, limit: PAGE_SIZE } })
      .then((res) => {
        setLogs(res.data.logs || []);
        setTotal(res.data.total || 0);
        setPage(p);
      })
      .catch((err) => setError(err.response?.data?.error || t('errors.generic')))
      .finally(() => setLoading(false));
  }, [orgId, t]);

  useEffect(() => { load(1); }, [load]);

  if (loading) return <p>{t('common.loading')}</p>;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <h2 className="sgc-admin__section-title">{t('admin.tabs.audit')}</h2>
      {error && <p className="sgc-admin__error">{error}</p>}

      <Card padded={false}>
        {logs.length === 0 ? (
          <EmptyState icon={ListIcon} title={t('admin.audit.emptyTitle')} description={t('admin.audit.emptyDescription')} />
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('admin.audit.col.when')}</th>
                <th>{t('admin.audit.col.action')}</th>
                <th>{t('admin.audit.col.ip')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="sgc-admin__muted">{new Date(log.timestamp).toLocaleString()}</td>
                  <td>{t(`admin.audit.actions.${log.action}`, log.action)}</td>
                  <td className="sgc-admin__muted">{log.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="sgc-admin__row-actions">
          <PillButton size="sm" variant="secondary" disabled={page <= 1} onClick={() => load(page - 1)}>{t('common.back')}</PillButton>
          <span className="sgc-admin__muted">{page} / {totalPages}</span>
          <PillButton size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => load(page + 1)}>{t('common.next')}</PillButton>
        </div>
      )}
    </div>
  );
}

export default Audit;
